import React, { FC } from 'react';
import styled from '@emotion/styled';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { IBogOgIdeBusinessProductISanitized, IPriceCustomerSpecificPrice } from '$models';
import { ProductImage } from '$components/elements/product-image';
import { isSpecialPrice, mq } from '$lib/helpers';
import { cover, imageTint } from '$lib/style-helpers';
import { ProductCardLayout } from './components/product-card-layout';
import { Link } from '../link';
import { trackHelloRetailClick } from '$services/hello-retail-tracking.service';

type ExtraData = {
    eagerLoad?: boolean;
    resourceID?: string;
    forcedSpecialPrice?: boolean;
    trackingCode?: string;
    onClickSideEffect?: () => void;
};

export const ProductCard: FC<IBogOgIdeBusinessProductISanitized & ExtraData> = ({
    pricesSanitized,
    eagerLoad,
    forcedSpecialPrice,
    trackingCode,
    onClickSideEffect,
    ...product
}: IBogOgIdeBusinessProductISanitized & ExtraData) => {
    const hasSpecialPrice = (prices: IPriceCustomerSpecificPrice[]) => {
        return prices.some((price) => isSpecialPrice(price.accountRelation));
    };

    const handleClick = async (source?: string) => {
        onClickSideEffect?.();
        if (!source) {
            return;
        }
        trackHelloRetailClick(source);
    };

    return (
        <Card>
            <ProductCardLayout
                image={
                    <StyledProductImage
                        eagerLoad={eagerLoad}
                        isOffer={
                            !!pricesSanitized?.actualPriceAmount &&
                            pricesSanitized.actualPriceAmount < (pricesSanitized.retailPriceAmount || 0)
                        }
                        itemGiftShop={product.itemGiftShop || false}
                        b2CDeliveryMethod={product.b2CDeliveryMethod}
                        imageUrl={product.primaryImageUrl}
                        resourceID={product.resourceID}
                        splashes={product.itemPriceTagUrl ? [product.itemPriceTagUrl] : []}
                        productName={product.name}
                        splashAltText={product.itemPriceTagKey}
                    />
                }
                name={product.nameClean || product.name}
                format={product.itemBinding}
                author={product.maker}
                prices={pricesSanitized}
                review={product.reviewScore}
                reviewTotal={product.numberOfReviews}
                hasSpecialPrice={
                    forcedSpecialPrice
                        ? forcedSpecialPrice
                        : !!product?.pricesRaw?.length && hasSpecialPrice(product.pricesRaw)
                }
            />
            <CoverLink href={product.productUrl} onClick={trackingCode ? () => handleClick(trackingCode) : undefined}>
                <VisuallyHidden.Root>{product.nameClean || product.name}</VisuallyHidden.Root>
            </CoverLink>
        </Card>
    );
};

const StyledProductImage = styled(ProductImage)(({ theme }) => ({
    ...imageTint(theme),
    padding: theme.space[4],
    [mq('md')]: {
        padding: theme.space[9],
    },
    '&:before': {
        content: '""',
        ...cover,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        borderRadius: theme.general.borderRadius,
        opacity: 0,
        transition: 'opacity 200ms ease',
        zIndex: 1,
    },
}));

const CoverLink = styled(Link)({
    ...cover,
    zIndex: 1,
});

const Card = styled.div(({ theme }) => ({
    position: 'relative',
    color: theme.colors.black,
    height: '100%',

    [`&:hover ${StyledProductImage}:before`]: {
        opacity: 1,
    },
}));
